/* global Excel */

/**
 * Generic write/read helpers for the resource-catalog Excel round-trip.
 *
 * Each entity (units, classifications, products, suppliers, offers) gets a
 * dedicated sheet name. `writeSheet` clears + repopulates; `readSheet` parses
 * the used range back into row objects keyed by the visible header name.
 */

import { formatCodeColumnsAsText } from "./sheetUtils";

const HEADER_FILL = "#0F766E";
const HEADER_FONT = "#FFFFFF";

// ── Sheet names + headers per entity ────────────────────────────────

export const UNITS_SHEET = "Units";
export const SUPPLIERS_SHEET = "Suppliers";
export const OFFERS_SHEET = "SupplierOffers";

export function classificationsSheetName(kind) { return `Classifications_${kind}`; }
export function productsSheetName(kind)        { return `Products_${kind}`; }

export const UNITS_HEADERS = [
  "Code", "Dimension", "Symbol",
  "Name_GEO", "Name_ENG", "Name_RUS",
  "Factor_to_Base", "Is_Base", "Sort_Order",
];

export const CLASSIFICATIONS_HEADERS = [
  "Code", "Kind", "Level", "Parent_Code",
  "Name_GEO", "Name_ENG", "Name_RUS",
  "Synonym_GEO", "Synonym_ENG", "Synonym_RUS",
  "GESN_Code", "ETIM_Code", "CPV_Code",
];

export const PRODUCTS_HEADERS = [
  "ID", "Kind", "Display_ID", "Classification_Code", "Is_Generic",
  "Name_GEO", "Name_ENG", "Name_RUS",
  "Description_GEO", "Description_ENG", "Description_RUS",
  "Base_Unit", "Base_Unit_Value",
  "Secondary_Unit", "Secondary_Unit_Factor",
  "Purchase_Unit", "Purchase_Unit_Factor",
  "Density_Unit", "Density",
  "Length", "Width", "Height", "Thickness", "Diameter",
  "Weight_per_Unit",
  "Discipline",
  "Brand_Name", "Product_Model", "Manufacturer", "Manufacturing_Location",
  "Costy_Code", "GESN_Code", "ETIM_Code", "UniFormat_Code", "OmniClass_Code", "MasterFormat_Code",
  "Status", "Public_ID", "Internal_ID",
];

export const SUPPLIERS_HEADERS = [
  "ID", "Name", "INN",
  "Contact_Person", "Contact_Email", "Contact_Phone",
  "Address", "Website", "Payment_Terms", "Rating",
  "Notes", "Is_Active",
];

export const OFFERS_HEADERS = [
  "ID", "Supplier_ID", "Supplier_Name",
  "Product_ID", "Product_Display_ID", "Product_Name",
  "Unit_Rate", "Currency",
  "Min_Order_Qty", "Max_Qty", "Lead_Time_Days",
  "Valid_From", "Valid_To", "Is_Preferred", "Note",
];

// ── Sheet primitives ────────────────────────────────────────────────

async function ensureSheet(context, name) {
  const wb = context.workbook;
  let sheet = wb.worksheets.getItemOrNullObject(name);
  sheet.load("name");
  await context.sync();
  if (sheet.isNullObject) {
    sheet = wb.worksheets.add(name);
    await context.sync();
  } else {
    const used = sheet.getUsedRangeOrNullObject();
    used.load("isNullObject");
    await context.sync();
    if (!used.isNullObject) {
      used.clear();
      await context.sync();
    }
  }
  return sheet;
}

function styleHeader(sheet, totalRows, totalCols) {
  const header = sheet.getRangeByIndexes(0, 0, 1, totalCols);
  header.format.fill.color = HEADER_FILL;
  header.format.font.color = HEADER_FONT;
  header.format.font.bold = true;
  header.format.horizontalAlignment = "Center";
}

function fillSheet(sheet, headers, rows) {
  const totalRows = 1 + rows.length;
  const totalCols = headers.length;
  const data = [headers, ...rows.map((r) => headers.map((h) => {
    const v = r[h];
    return v === undefined || v === null ? "" : v;
  }))];
  formatCodeColumnsAsText(sheet, headers, totalRows);
  sheet.getRangeByIndexes(0, 0, totalRows, totalCols).values = data;
  styleHeader(sheet, totalRows, totalCols);
  sheet.freezePanes.freezeRows(1);
}

async function readSheetRows(context, sheetName) {
  const wb = context.workbook;
  const sheet = wb.worksheets.getItemOrNullObject(sheetName);
  sheet.load("name");
  await context.sync();
  if (sheet.isNullObject) {
    throw new Error(`Sheet "${sheetName}" not found. Click "Load to Excel" first.`);
  }
  const used = sheet.getUsedRangeOrNullObject();
  used.load(["values", "isNullObject"]);
  await context.sync();
  if (used.isNullObject || !used.values || used.values.length < 2) return [];

  const [headerRow, ...bodyRows] = used.values;
  const headers = headerRow.map((h) => (h == null ? "" : String(h).trim()));

  return bodyRows
    .map((r) => {
      const obj = {};
      let nonEmpty = false;
      headers.forEach((h, i) => {
        const v = r[i];
        if (v !== null && v !== undefined && v !== "") nonEmpty = true;
        obj[h] = v;
      });
      return nonEmpty ? obj : null;
    })
    .filter(Boolean);
}

// ── Per-entity helpers ──────────────────────────────────────────────

export async function writeUnitsToExcel(units) {
  await Excel.run(async (context) => {
    const sheet = await ensureSheet(context, UNITS_SHEET);
    const rows = (units || []).map((u) => ({
      Code: u.code, Dimension: u.dimension, Symbol: u.symbol,
      Name_GEO: u.name_geo, Name_ENG: u.name_eng, Name_RUS: u.name_rus,
      Factor_to_Base: u.factor_to_base, Is_Base: u.is_base ? "YES" : "", Sort_Order: u.sort_order,
    }));
    fillSheet(sheet, UNITS_HEADERS, rows);
    sheet.activate();
    sheet.getUsedRange().format.autofitColumns();
    await context.sync();
  });
  return UNITS_SHEET;
}

export async function readUnitsFromExcel() {
  let result = [];
  await Excel.run(async (context) => {
    result = await readSheetRows(context, UNITS_SHEET);
  });
  return result;
}

export async function writeClassificationsToExcel(kind, classifications) {
  const sheetName = classificationsSheetName(kind);
  await Excel.run(async (context) => {
    const sheet = await ensureSheet(context, sheetName);
    const rows = (classifications || []).map((c) => ({
      Code: c.code, Kind: c.kind, Level: c.level, Parent_Code: c.parent_code,
      Name_GEO: c.name_geo, Name_ENG: c.name_eng, Name_RUS: c.name_rus,
      Synonym_GEO: c.synonym_geo, Synonym_ENG: c.synonym_eng, Synonym_RUS: c.synonym_rus,
      GESN_Code: c.gesn_code, ETIM_Code: c.etim_code, CPV_Code: c.cpv_code,
    }));
    fillSheet(sheet, CLASSIFICATIONS_HEADERS, rows);
    sheet.activate();
    sheet.getUsedRange().format.autofitColumns();
    await context.sync();
  });
  return sheetName;
}

export async function readClassificationsFromExcel(kind) {
  const sheetName = classificationsSheetName(kind);
  let result = [];
  await Excel.run(async (context) => {
    result = await readSheetRows(context, sheetName);
  });
  return result;
}

export async function writeProductsToExcel(kind, products) {
  const sheetName = productsSheetName(kind);
  await Excel.run(async (context) => {
    const sheet = await ensureSheet(context, sheetName);
    const rows = (products || []).map((p) => ({
      ID: p.id, Kind: p.kind,
      Display_ID: p.display_id,
      Classification_Code: p.classification_code,
      Is_Generic: p.is_generic ? "YES" : "",
      Name_GEO: p.name_geo, Name_ENG: p.name_eng, Name_RUS: p.name_rus,
      Description_GEO: p.description_geo, Description_ENG: p.description_eng, Description_RUS: p.description_rus,
      Base_Unit: p.base_unit, Base_Unit_Value: p.base_unit_value,
      Secondary_Unit: p.secondary_unit, Secondary_Unit_Factor: p.secondary_unit_factor,
      Purchase_Unit: p.purchase_unit, Purchase_Unit_Factor: p.purchase_unit_factor,
      Density_Unit: p.density_unit, Density: p.density,
      Length: p.length, Width: p.width, Height: p.height,
      Thickness: p.thickness, Diameter: p.diameter,
      Weight_per_Unit: p.weight_per_unit,
      Discipline: p.discipline,
      Brand_Name: p.brand_name, Product_Model: p.product_model,
      Manufacturer: p.manufacturer, Manufacturing_Location: p.manufacturing_location,
      Costy_Code: p.costy_code, GESN_Code: p.gesn_code, ETIM_Code: p.etim_code,
      UniFormat_Code: p.uniformat_code, OmniClass_Code: p.omniclass_code, MasterFormat_Code: p.masterformat_code,
      Status: p.status, Public_ID: p.public_id, Internal_ID: p.internal_id,
    }));
    fillSheet(sheet, PRODUCTS_HEADERS, rows);
    sheet.activate();
    sheet.getUsedRange().format.autofitColumns();
    await context.sync();
  });
  return sheetName;
}

export async function readProductsFromExcel(kind) {
  const sheetName = productsSheetName(kind);
  let result = [];
  await Excel.run(async (context) => {
    const rows = await readSheetRows(context, sheetName);
    // Default Kind from sheet name if user left it blank
    result = rows.map((r) => ({ Kind: r.Kind || kind, ...r }));
  });
  return result;
}

export async function writeSuppliersToExcel(suppliers) {
  await Excel.run(async (context) => {
    const sheet = await ensureSheet(context, SUPPLIERS_SHEET);
    const rows = (suppliers || []).map((s) => ({
      ID: s.id, Name: s.name, INN: s.inn,
      Contact_Person: s.contact_person, Contact_Email: s.contact_email, Contact_Phone: s.contact_phone,
      Address: s.address, Website: s.website, Payment_Terms: s.payment_terms,
      Rating: s.rating, Notes: s.notes, Is_Active: s.is_active ? "YES" : "",
    }));
    fillSheet(sheet, SUPPLIERS_HEADERS, rows);
    sheet.activate();
    sheet.getUsedRange().format.autofitColumns();
    await context.sync();
  });
  return SUPPLIERS_SHEET;
}

export async function readSuppliersFromExcel() {
  let result = [];
  await Excel.run(async (context) => {
    result = await readSheetRows(context, SUPPLIERS_SHEET);
  });
  return result;
}

export async function writeOffersToExcel(offers) {
  await Excel.run(async (context) => {
    const sheet = await ensureSheet(context, OFFERS_SHEET);
    const rows = (offers || []).map((o) => ({
      ID: o.id,
      Supplier_ID: o.supplier_id, Supplier_Name: o.supplier_name,
      Product_ID: o.product_id, Product_Display_ID: o.product_display_id,
      Product_Name: o.product_name_geo || o.product_name_eng || "",
      Unit_Rate: o.unit_rate, Currency: o.currency,
      Min_Order_Qty: o.min_order_qty, Max_Qty: o.max_qty, Lead_Time_Days: o.lead_time_days,
      Valid_From: o.valid_from, Valid_To: o.valid_to,
      Is_Preferred: o.is_preferred ? "YES" : "",
      Note: o.note,
    }));
    fillSheet(sheet, OFFERS_HEADERS, rows);
    sheet.activate();
    sheet.getUsedRange().format.autofitColumns();
    await context.sync();
  });
  return OFFERS_SHEET;
}

export async function readOffersFromExcel() {
  let result = [];
  await Excel.run(async (context) => {
    result = await readSheetRows(context, OFFERS_SHEET);
  });
  return result;
}
